import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { Grid } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},

    textCenter: {
        backgroundColor: 'red'
    }
}));

// Función para detectar si un contacto es de webchat
const isWebChatContact = (number) => {
	return number && number.startsWith('webchat:');
};

// Esquema de validación dinámico
const getContactSchema = (isWebChat) => {
	const baseSchema = {
		name: Yup.string()
			.min(2, "¡Muy corto!")
			.max(100, "¡Demasiado largo!")
			.required("Requerido"),
		email: Yup.string().email("Correo electrónico inválido"),
	};

	if (!isWebChat) {
		// Permitimos null/undefined para que el backend pueda aceptar contactos sin número.
		// Si llega string vacío desde la UI, lo tratamos como null antes de validar.
		baseSchema.number = Yup.string()
			.nullable()
			.transform((value) => (value === "" ? null : value))
			.min(8, "¡Muy corto!")
			.max(100, "¡Demasiado largo!");
	}

	return Yup.object().shape(baseSchema);
};

export function ContactForm ({ initialContact, onSave, onCancel }) {
	const classes = useStyles();
	const history = useHistory();
	const [contact, setContact] = useState(initialContact);
	
	// Detectar si es contacto de webchat
	const isWebChat = isWebChatContact(contact?.number);

    useEffect(() => {
        setContact(initialContact);
    }, [initialContact]);

	const handleSaveContact = async (values, actions) => {
		try {
			// Si es webchat, mantener el número original
			const rawNumber = isWebChat ? contact.number : values.number;
			// Convertir string vacío a null para que el backend acepte contactos sin número.
			const normalizedNumber =
				rawNumber === undefined || rawNumber === null || (typeof rawNumber === "string" && rawNumber.trim() === "")
					? null
					: rawNumber;

			const dataToSend = {
				...values,
				number: normalizedNumber,
			};

			if (contact.id) {
				await api.put(`/contacts/${contact.id}`, {
                    channel: dataToSend.channel,
                    email: dataToSend.email,
                    hasWhatsappAccount: dataToSend.hasWhatsappAccount,
                    isGroup: dataToSend.isGroup,
                    name: dataToSend.name,
                    number: dataToSend.number,
                    profilePicUrl: dataToSend.profilePicUrl,
                });
				if (onSave) {
					onSave(dataToSend);
				}
			} else {
				const { data } = await api.post("/contacts", dataToSend);
				if (onSave) {
					onSave(data);
				}
			}
			toast.success(i18n.t("contactModal.success"));
			if (onCancel) {
				onCancel();
			}
		} catch (err) {
			toastError(err);
		}
	};

    return (
        <Formik
            initialValues={contact}
            enableReinitialize={true}
            validationSchema={getContactSchema(isWebChat)}
            onSubmit={(values, actions) => {
                setTimeout(() => {
                    handleSaveContact(values, actions);
                    actions.setSubmitting(false);
                }, 400);
            }}
        >
            {({ values, errors, touched, isSubmitting }) => (
                <Form>
                    <Grid container spacing={1}>
                        {/* <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                {i18n.t("contactModal.form.mainInfo")}
                            </Typography>
                        </Grid> */}
                        <Grid item xs={12}>
                            <Field
                                as={TextField}
                                label={i18n.t("contactModal.form.name")}
                                name="name"
                                autoFocus
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                variant="outlined"
                                margin="dense"
                                className={classes.textField}
                                fullWidth
                            />
                        </Grid>
                        
                        {/* Solo mostrar el campo de número si NO es contacto de webchat */}
                        {!isWebChat && (
                            <Grid item xs={12}>
                                <Field
                                    as={TextField}
                                    label={i18n.t("contactModal.form.number")}
                                    name="number"
                                    error={touched.number && Boolean(errors.number)}
                                    helperText={touched.number && errors.number}
                                    placeholder="5513912344321"
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                            </Grid>
                        )}
                        
                        <Grid item xs={12}>
                            <Field
                                as={TextField}
                                label={i18n.t("contactModal.form.email")}
                                name="email"
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                                placeholder="Email address"
                                fullWidth
                                margin="dense"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} spacing={1}>
                            <Grid container spacing={1}>
                                <Grid xs={6} item>
                                    <Button
                                        onClick={onCancel}
                                        color="secondary"
                                        disabled={isSubmitting}
                                        variant="outlined"
                                        fullWidth
                                    >
                                        {i18n.t("contactModal.buttons.cancel")}
                                    </Button>
                                </Grid>
                                <Grid classes={classes.textCenter} xs={6} item>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        disabled={isSubmitting}
                                        variant="contained"
                                        className={classes.btnWrapper}
                                        fullWidth
                                    >
                                        {contact.id
                                            ? `${i18n.t("contactModal.buttons.okEdit")}`
                                            : `${i18n.t("contactModal.buttons.okAdd")}`}
                                        {isSubmitting && (
                                            <CircularProgress
                                                size={24}
                                                className={classes.buttonProgress}
                                            />
                                        )}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}
